"use client"

import { usePathname } from "next/navigation"
import { useUserRole } from "@/providers/user-role-provider"
import { NotificationsPanel } from "@/components/notifications-panel"
import { BarberNotificationsPanel } from "@/components/barber-notifications-panel"
import { ErrorBoundary } from "@/components/error-boundary"
import { useNavTheme } from "@/hooks/use-nav-theme"
import { stripLocalePrefix, isGlassNavPath, isChromelessPath } from "@/i18n/routing"

// Mobile-only floating notifications bell. Sits opposite the BOOTH
// wordmark (top-center) so the top of every authenticated mobile
// page reads: [bare logo] ───── [bell badge].
//
// Always visible (does not fade on scroll like the logo) — the unread
// badge is functional state the user needs to see at a glance, not
// decoration. Tablet+ already gets a notifications panel via
// DashboardNavbarMenu / the desktop strip, so this component is
// strictly md:hidden.

// Mirrors DesktopNotificationsHeader's rules so the bell appears on the same
// pages at every breakpoint. The landing/changelog/traction pages are
// marketing surfaces that own their own top-right corner (PublicNavbar's
// menu + Join) — app chrome on top of that reads as a bug, and did.
const HIDE_PREFIXES: ReadonlyArray<string> = ["/auth", "/clients"]

function shouldHide(rawPathname: string | null): boolean {
  if (!rawPathname) return false
  // Locale-agnostic: the Spanish landing at /es must hide exactly like /.
  const pathname = stripLocalePrefix(rawPathname)
  // Pages the glass nav owns carry the whole site in their menu panel — this
  // would be a second, competing one on the same screen.
  if (isChromelessPath(pathname)) return true
  if (isGlassNavPath(pathname)) return true
  return HIDE_PREFIXES.some(p => pathname === p || pathname.startsWith(`${p}/`))
}

export function MobileNotificationsHeader() {
  const pathname = usePathname()
  const { userId, role, actualUserType, isLoading } = useUserRole()
  // Same theme observer the floating wordmark uses — keeps the bell
  // legible whether it's floating over a dark hero or a light section.
  const theme = useNavTheme(pathname === "/" ? "dark" : "light")

  if (shouldHide(pathname)) return null
  if (isLoading) return null
  // Only authenticated users have notifications. Logged-out visitors
  // see nothing on the right side — keeps the public surface clean.
  if (!userId) return null

  // Active mode wins over the stored role. A dual-role user (an owner who
  // also cuts) standing in Barber mode was getting the OWNER panel, because
  // their DB role is 'owner' — so an appointment notification routed them to
  // the owner dashboard instead of their bookings.
  const effective = role ?? actualUserType
  const isBarber = effective === "barber"

  // Arbitrary descendant variant overrides the panel's internal
  // `text-foreground` on the trigger button without having to plumb a
  // prop through both NotificationsPanel and BarberNotificationsPanel.
  const tint = theme === "dark" ? "[&_button]:!text-white" : ""

  return (
    <div className={`md:hidden fixed safe-top-bell right-2 z-40 ${tint}`}>
      <ErrorBoundary>
        {isBarber ? <BarberNotificationsPanel /> : <NotificationsPanel />}
      </ErrorBoundary>
    </div>
  )
}
